import React from "react"
import PropTypes from "prop-types"
import { Column } from "react-virtualized"
import moment from "moment"
import bs from "binary-search"
import { ComplianceCircle } from "@swipesense/compliance-helpers"
import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"
import HelpToolTip from "@swipesense/help-tooltip"
import { entitiesPropTypes } from "../../utils/propTypes"
import { exposureSearcher } from "../../utils/search"
import { basicSort } from "../../utils/sort"
import PageBanner from "../PageBanner"
import Loading from "../Loading"
import "./ExposedLocationVisitList.scss"
require("moment-duration-format") // not called directly, but extends moment to add moment.duration()

const ExposedLocationVisitList = (props) => {
  /* eslint-disable react/prop-types */
  const isExposureEvent = (visit) => {
    const matchingLocations = props.entities.filter(
      (entity) => entity.id === props.locationId
    )
    return matchingLocations.length > 0
      ? bs(matchingLocations, visit, exposureSearcher) >= 0
      : false
  }
  /* eslint-enable react/prop-types */

  const sortedVisits = !props.loading
    ? props.visits
        .map((visit) => ({
          ...visit,
          entryAt: moment.parseZone(visit.entryAt),
          subjectUserId: props.userId,
        }))
        .sort(basicSort("startAt", "DESC"))
    : []

  const nameCellRenderer = (props) => {
    return (
      <div className="nameCell">
        <div className="name">{props.cellData}</div>
      </div>
    )
  }

  nameCellRenderer.propTypes = {
    cellData: PropTypes.string,
    rowData: PropTypes.object,
  }

  const visitCellRenderer = (props) => {
    const visitDate = moment.parseZone(props.rowData.entryAt)
    const duration = moment.duration(props.rowData.duration, "seconds")
    return (
      <div className="visit">
        <div className="date">{visitDate.format("MMM D")}</div>
        <div className="dot-divider">•</div>
        <div className="time">{visitDate.format("h:mm a").slice(0, -1)}</div>
        <div className="duration">{duration.format("(h[h] m[m])")}</div>
      </div>
    )
  }

  visitCellRenderer.propTypes = {
    rowData: PropTypes.object,
  }

  /* eslint-disable react/prop-types */
  const exposureCellRenderer = (props) => {
    // If a visit is an exposure (the visit overlaps with a visit by the subject of the case)
    // and the user visiting the location **is** the subject of the case, mark it as an exposure
    // event, otherwise, mark it as a "Staff Overlap". If the visit does not overlap with a
    // visit by the subject, it is not an exposure.
    if (isExposureEvent(props.rowData)) {
      return props.rowData.userId === props.rowData.subjectUserId ? (
        <div className="exposure-cell">
          <div className="exposure"> !</div>Exposure{" "}
        </div>
      ) : (
        <div className="staff-overlap">Staff Overlapped</div>
      )
    } else {
      return ""
    }
  }
  /* eslint-enable react/prop-types */

  const rowHighlighter = (row) => {
    // Highlight a row only if it is an exposure event **and**
    // the visit to the location is by the subject of the case.
    const visit = sortedVisits[row.index]
    if (row.index === -1 || !isExposureEvent(visit)) {
      return ""
    } else {
      return visit.userId === props.userId ? "exposed-row" : ""
    }
  }

  return (
    <div className="ExposedLocationVisitList">
      <PageBanner className={"exposed-location-visit-list-banner"}>
        <div className="exposed-location-visit-header">
          <div className="room-name">
            <h1>{props.name}</h1>
          </div>
          <div className="room-details">
            <div className="unit-name">{props.unit}</div>
            {props.compliance && (
              <div className="compliance">
                <div className="top-text">
                  COMPLIANCE
                  <HelpToolTip>
                    Overall hand hygiene compliance for duration of exposure
                    time period.
                  </HelpToolTip>
                </div>
                <div className="circle">
                  <ComplianceCircle compliance={props.compliance} />
                  {props.compliance.toFixed(0)}%
                </div>
              </div>
            )}
          </div>
        </div>
      </PageBanner>
      <div className="visits-wrapper">
        <DataTable
          className="exposed-location-visit-table"
          rowHeight={46}
          data={sortedVisits}
          loading={props.loading}
          loadingComponent={<Loading />}
          rowClassName={rowHighlighter}
          noRowsMessage={"No visits found."}
        >
          <Column
            key="name"
            dataKey="userName"
            width={25}
            label="Name"
            cellRenderer={nameCellRenderer}
            disableSort={true}
          />
          <Column
            key="entryAt"
            dataKey="entryAt"
            width={45}
            label="Visit Duration"
            cellRenderer={visitCellRenderer}
            disableSort={true}
          />
          <Column
            key="exposureEvent"
            dataKey="exposureEvent"
            width={15}
            label="Exposure Event"
            headerRenderer={tooltipHeaderRenderer(
              '"Exposed" indicates a visit to a room occupied by a contagious patient or employee.'
            )}
            cellRenderer={exposureCellRenderer}
            disableSort={true}
          />
        </DataTable>
      </div>
      <div className="total-results">
        {props.visits && (
          <div>
            {props.visits.length}{" "}
            {props.visits.length === 1 ? "result" : "results"}
          </div>
        )}
      </div>
    </div>
  )
}

ExposedLocationVisitList.propTypes = {
  userId: PropTypes.string,
  entities: entitiesPropTypes,
  name: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  compliance: PropTypes.number.isRequired,
  locationId: PropTypes.string.isRequired,
  visits: PropTypes.arrayOf(
    PropTypes.shape({
      duration: PropTypes.number,
      entryAt: PropTypes.string,
      exitAt: PropTypes.string,
      userName: PropTypes.string,
      userId: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
}

ExposedLocationVisitList.defaultProps = {
  entities: [],
  visits: [],
}

export default ExposedLocationVisitList
